import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Autocomplete from "@mui/material/Autocomplete";
import ReplyIcon from "@mui/icons-material/Reply";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Rating from "@mui/material/Rating";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import http from "../../http";
import CommonDataService from "../../services/common";
import {
	AllowedUsergroup,
	parseDate,
	GetIndexByValue,
	clean,
	parseNumber,
} from "../../helpers/common";
import {
	setCurrentPageSize,
	setLoading,
	setModule,
} from "../../store/actions/commonAction";

import MessageDialog from "../MessageDialog";
import Pager from "../Pager";

export default function ReviewsList(event) {
	const [searchParams, setSearchParams] = useSearchParams();

	let currentPage = clean(searchParams.get("page"));
	if (currentPage === "") {
		currentPage = 1;
	}

	let s = clean(searchParams.get("s"));
	if (s === "") {
		s = "v.publish_time";
	}

	let d = clean(searchParams.get("d"));
	if (d === "") {
		d = "desc";
	}

	const name = useRef(clean(searchParams.get("name")));
	const rating = useRef(clean(searchParams.get("rating")));
	const comment = useRef(clean(searchParams.get("comment")));
	const reply = useRef(clean(searchParams.get("reply")));
	const orderBy = useRef(s);
	const order = useRef(d);

	const [rows, setRows] = useState([]);
	const [count, setCount] = useState(0);
	const [message, setMessage] = useState("");
	const [error, setError] = useState(false);
	const [rep, setRep] = useState(false);
	const [repID, setRepID] = useState(0);
	const [repText, setRepText] = useState("");
	const [page, setPage] = useState(parseNumber(currentPage));

	const [offices, setOffices] = useState([]);
	const [idOffice, setIdOffice] = useState(clean(searchParams.get("office")));
	const [idOfficeAC, setIdOfficeAC] = useState(null);

	const currentPageSize = useSelector(
		(state) => state.commonReducer.currentPageSize
	);

	const userGroup = useSelector((state) => state.commonReducer.user.usergroup);
	const orders = useSelector((state) => state.commonReducer.orders);
	const pageSizes = useSelector((state) => state.commonReducer.pageSizes);

	const dispatch = useDispatch();

	const sorts = [
		{
			label: "Update Time",
			value: "r.updateTime",
		},
		{
			label: "Rating",
			value: "r.rating",
		},
		{
			label: "Reply",
			value: "r.reviewReplyComment",
		},
	];

	const LoadData = useCallback(() => {
		let pars = {
			name: name.current,
			rating: rating.current,
			comment: comment.current,
			reply: reply.current,
			office: idOffice,
			s: orderBy.current,
			d: order.current,
			page: page,
			perpage: currentPageSize,
		};

		dispatch(setLoading(true));
		CommonDataService.getReviews(pars)
			.then((res) => {
				setRows(res.data);
				setCount(res.data.total_pages);
				dispatch(setLoading(false));
			})
			.catch(function (error) {
				setMessage(error.response.data.message);
				setError(true);
				dispatch(setLoading(false));
			});
	}, [dispatch, idOffice, page, currentPageSize]);

	useEffect(() => {
		LoadData();
	}, [LoadData, dispatch, idOffice, page, currentPageSize]);

	const LoadSelect = useCallback(() => {
		if (AllowedUsergroup(userGroup, "superuser-website")) {
			CommonDataService.getOffices().then((res) => {
				setOffices(res.data);
			});
		}
	}, [userGroup]);

	useEffect(() => {
		LoadSelect();
	}, [LoadSelect]);

	useEffect(() => {
		setIdOfficeAC(GetIndexByValue(offices, idOffice));
	}, [offices, idOffice]);

	useEffect(() => {
		dispatch(setModule("Google Reviews"));
	}, [dispatch]);

	const handleSubmit = (event) => {
		event.preventDefault();
		const data = new FormData(event.currentTarget);
		name.current = data.get("name");
		rating.current = data.get("rating");
		comment.current = data.get("comment");
		reply.current = data.get("reply");
		orderBy.current = data.get("orderBy");
		order.current = data.get("order");
		setPage(1);
		setSearchParams(searchParams);
		LoadData();
	};

	const OnChangeOffices = (e, val) => {
		if (val !== null) {
			setIdOffice(val.id);
		} else {
			setIdOffice("");
		}
		setPage(1);
	};

	const replyForm = (id, title) => {
		setRep(true);
		setMessage(title);
		setRepText("");
		setRepID(id);
	};

	const repClose = () => {
		setRep(false);
		setRepID(0);
	};

	const sendReply = () => {
		setRep(false);
		dispatch(setLoading(true));

		http
			.post("/reviews/savereply", {
				id: repID,
				reply: repText,
			})
			.then((res) => {
				dispatch(setLoading(false));
				LoadData();
			})
			.catch(function (error) {
				setMessage(error.response.data.message);
				setError(true);
				dispatch(setLoading(false));
			});
	};

	return (
		<Grid container spacing={3}>
			<Grid item xs={12}>
				<Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
					<Box component="form" onSubmit={(e) => handleSubmit(e)}>
						<Typography variant="h5">Search Google Reviews Form</Typography>

						<Grid container spacing={3}>
							{AllowedUsergroup(userGroup, "superuser-website") && (
								<Grid item xs={12} sm={12}>
									<Autocomplete
										options={offices}
										getOptionLabel={(option) => option.name}
										fullWidth={true}
										onChange={OnChangeOffices}
										value={idOfficeAC}
										sx={{ minWidth: 100 }}
										renderInput={(params) => (
											<TextField
												variant="standard"
												{...params}
												label="Office"
											/>
										)}
									/>
								</Grid>
							)}

							<Grid item xs={12} sm={6}>
								<TextField
									label="Search by Reviewer Name"
									id="name"
									name="name"
									fullWidth
									defaultValue={name.current}
									variant="standard"
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<TextField
									label="Search by Comment"
									id="comment"
									name="comment"
									fullWidth
									defaultValue={comment.current}
									variant="standard"
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<TextField
									label="Search by Reply"
									id="reply"
									name="reply"
									fullWidth
									defaultValue={reply.current}
									variant="standard"
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<FormControl
									fullWidth={true}
									variant="standard"
									sx={{ minWidth: 100 }}
								>
									<InputLabel id="id-active-label">Rating</InputLabel>
									<Select
										id="rating"
										name="rating"
										labelId="id-active-label"
										defaultValue={rating.current}
									>
										<MenuItem value="">
											<em>All</em>
										</MenuItem>
										<MenuItem value="5">5</MenuItem>
										<MenuItem value="4">4</MenuItem>
										<MenuItem value="3">3</MenuItem>
										<MenuItem value="2">2</MenuItem>
										<MenuItem value="1">1</MenuItem>
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} sm={3}>
								<FormControl
									fullWidth={true}
									variant="standard"
									sx={{ minWidth: 100 }}
								>
									<InputLabel id="orderbylabel">Order by</InputLabel>
									<Select
										id="orderBy"
										name="orderBy"
										labelId="orderbylabel"
										label="Order By"
										defaultValue={orderBy.current}
									>
										{sorts.map((option, index) => (
											<MenuItem key={index} value={option.value}>
												{option.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} sm={3}>
								<FormControl
									fullWidth={true}
									variant="standard"
									sx={{ minWidth: 100 }}
								>
									<InputLabel id="orderlabel">Sort</InputLabel>
									<Select
										id="order"
										name="order"
										labelId="orderlabel"
										label="Sort"
										defaultValue={order.current}
									>
										{orders.map((option, index) => (
											<MenuItem key={index} value={option.value}>
												{option.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} sm={12}></Grid>

							<Grid item xs={12} sm={12}>
								<Button
									type="submit"
									variant="contained"
									startIcon={<SearchIcon />}
								>
									Search
								</Button>
							</Grid>
						</Grid>
					</Box>
				</Paper>
			</Grid>

			<Grid item xs={12}>
				<Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
					<Typography variant="h4" gutterBottom component="div">
						Google Review List
					</Typography>

					<TableContainer component={Paper}>
						<Table sx={{}} size="small">
							<TableHead>
								<TableRow>
									<TableCell width={5}>No.</TableCell>
									<TableCell>Office</TableCell>
									<TableCell>Review</TableCell>
									<TableCell>Reply</TableCell>
									<TableCell align="center">Reply</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{rows.Data &&
									rows.Data.map((row, index) => (
										<TableRow
											key={index}
											sx={{
												"&:last-child td, &:last-child th": { border: 0 },
											}}
										>
											<TableCell align="right">
												{rows.first_no + index}
											</TableCell>
											<TableCell>{row.office}</TableCell>
											<TableCell>
												<Grid container spacing={0}>
													<Grid item xs={3} sm={3}>
														<img alt="" height="50" src={row.profile_photo} />
													</Grid>
													<Grid
														item
														xs={9}
														sm={9}
														container
														direction="row"
														justify="flex-end"
														alignItems="center"
													>
														<Typography variant="subtitle2" gutterBottom>
															{row.name}
														</Typography>
													</Grid>
												</Grid>

												<Typography
													variant="caption"
													display="block"
													gutterBottom
												>
													{parseDate(row.updateTime)}
												</Typography>
												<Rating name="read-only" value={row.rating} readOnly />
												<br />
												{row.comment}
											</TableCell>
											<TableCell>{row.reply}</TableCell>
											<TableCell align="center">
												<ReplyIcon
													fontSize="small"
													onClick={() => replyForm(row.id, row.name)}
												/>
											</TableCell>
										</TableRow>
									))}
							</TableBody>

							<Pager
								colSpan={6}
								count={count}
								dispatch={dispatch}
								page={page}
								pageSizes={pageSizes}
								currentPageSize={currentPageSize}
								setPage={setPage}
								setCurrentPageSize={setCurrentPageSize}
							/>
						</Table>
					</TableContainer>

					<MessageDialog
						show={error}
						message={message}
						dialogClose={setError}
					/>

					<Dialog open={rep} onClose={repClose} fullWidth={true}>
						<DialogTitle>Reply to {message}</DialogTitle>
						<DialogContent>
							<TextField
								label=""
								multiline
								rows={4}
								value={repText}
								fullWidth
								placeholder="Reply comment"
								variant="standard"
								onChange={(e) => setRepText(e.target.value)}
							/>
						</DialogContent>
						<DialogActions>
							<Button onClick={repClose}>Close</Button>
							<Button onClick={sendReply}>Send reply</Button>
						</DialogActions>
					</Dialog>
				</Paper>
			</Grid>
		</Grid>
	);
}
